import React, {useLayoutEffect, useState} from 'react';
import {
    Button,
    Card,
    CardBody,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Typography
} from "@material-tailwind/react";
import axios from "axios";
import LotSummary from "./lot_summary";

const FRANCHISE_ID = 15;
const FRANCHISE_EXPIRATION_ID = 24;

// Motivos 15 - FRANQUICIA y 24 - VENCIMIENTO DE FRANQUICIA (componente compartido).
// 15: registra el periodo exento (inicio requerido; sin fin = exención indefinida).
// 24: cierra la franquicia vigente fijando la fecha de fin (inicio de solo lectura).
// En ambos se regeneran los adeudos: los bimestres del periodo exento se eliminan
// y no se vuelven a generar (SP calculate_debts).
const Franchise = ({lot, movement}) => {
    const isExpiration = Number(movement.id) === FRANCHISE_EXPIRATION_ID;

    const [lotNotes, setLotNotes] = useState("");
    const [openEndDialog, setOpenEndDialog] = useState(false);
    const [loaderActive, setLoaderActive] = useState(false);
    const [error, setError] = useState(null);
    const [startExemptBimester, setStartExemptBimester] = useState("");
    const [startExemptYear, setStartExemptYear] = useState("");
    const [endExemptBimester, setEndExemptBimester] = useState("");
    const [endExemptYear, setEndExemptYear] = useState("");

    useLayoutEffect(() => {
        setStartExemptBimester(lot.start_exempt_bimester || "");
        setStartExemptYear(lot.start_exempt_year || "");
        setEndExemptBimester(lot.end_exempt_bimester || "");
        setEndExemptYear(lot.end_exempt_year || "");
        setLotNotes(lot.notes);
    }, [lot]);

    const getCSRFToken = () => {
        const token = document.querySelector('meta[name="csrf-token"]');
        return token ? token.content : "";
    };

    const handleReset = () => {
        window.location.replace('movements');
    };

    // El predio tiene franquicia registrada si tiene inicio o fin de exención.
    const hasFranchise = Boolean(lot.start_exempt_year || lot.end_exempt_year);

    const validate = () => {
        if (isExpiration) {
            if (!hasFranchise) {
                return 'El predio no tiene franquicia registrada.';
            }
            if (!endExemptBimester || !endExemptYear) {
                return 'Captura el bimestre y año de fin de la franquicia.';
            }
        } else {
            if (!startExemptBimester || !startExemptYear) {
                return 'Captura el bimestre y año de inicio de la franquicia.';
            }
        }
        // Si ambos extremos están presentes, el inicio no puede ser posterior al fin.
        if (startExemptYear && endExemptYear) {
            const start = Number(startExemptYear) * 10 + Number(startExemptBimester || 1);
            const end = Number(endExemptYear) * 10 + Number(endExemptBimester || 6);
            if (start > end) {
                return 'El inicio de la franquicia no puede ser posterior al fin.';
            }
        }
        return null;
    };

    const handleSubmit = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);

        try {
            setLoaderActive(true);
            const response = await axios.put(`lots/${lot.parcel_code}.json`, {
                lot: {
                    start_exempt_bimester: startExemptBimester || null,
                    start_exempt_year: startExemptYear || null,
                    end_exempt_bimester: endExemptBimester || null,
                    end_exempt_year: endExemptYear || null,
                    notes: lotNotes,
                    reason_type_id: movement.id,
                    calculate_debt: true,
                },
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    "X-CSRF-Token": getCSRFToken(),
                }
            });
            if (response.status === 200) {
                setOpenEndDialog(true);
                const url = `/lots/${lot.parcel_code}/print_movement.pdf`;
                window.open(url, '_blank');
                setLoaderActive(false);
            } else {
                setError('Error al actualizar la franquicia del predio. Verifica los datos.');
                setLoaderActive(false);
            }
        } catch (err) {
            setError('Error al guardar. Verifica los datos.');
            setLoaderActive(false);
        }
    };

    const bim = [1, 2, 3, 4, 5, 6];
    const currentYear = new Date().getFullYear();
    const startYears = Array.from({length: 21}, (_, i) => currentYear - 4 + i);
    // El fin de la exención no puede quedar en el pasado: inicia en el año actual.
    const endYears = startYears.filter((year) => year >= currentYear);

    return (
        <>
            <div className="flex-auto py-6 pt-0">
                <h6 className="font-semibold uppercase bg-blue-500 text-white p-2 rounded-t-lg">
                    Información del predio
                </h6>
                <div className="flex flex-col lg:flex-row gap-4 min-h-[350px]">
                    <div
                        className="flex-grow border border-blue-gray-100 p-4 rounded-t-none rounded-b-lg w-full lg:w-1/2 overflow-x-hidden">
                        <LotSummary lot={lot}/>
                    </div>
                </div>
            </div>
            <div className="flex-auto py-10 pt-0">
                <h6 className="font-semibold uppercase bg-blue-500 text-white p-2 rounded-t-lg">
                    {isExpiration ? 'Vencimiento de franquicia' : 'Franquicia'}
                </h6>
                <Card className='w-full border border-blue-gray-100 rounded-b-lg rounded-t-none' shadow={false}>
                    <CardBody className='p-4'>
                        {isExpiration && !hasFranchise && (
                            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
                                <Typography variant="small" className="font-medium text-red-600">
                                    El predio no tiene franquicia registrada.
                                </Typography>
                            </div>
                        )}
                        <div className='py-2'>
                            <Typography
                                variant="small"
                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                            >
                                Inicio de exención
                            </Typography>
                            {isExpiration ? (
                                <div className='flex flex-row gap-4 w-full text-center'>
                                    <div className='flex flex-col w-full'>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                        >
                                            Bimestre
                                        </Typography>
                                        <Typography variant="paragraph" className="font-medium">
                                            {lot.start_exempt_bimester || 'N/A'}
                                        </Typography>
                                    </div>
                                    <div className='flex flex-col w-full'>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                        >
                                            Año
                                        </Typography>
                                        <Typography variant="paragraph" className="font-medium">
                                            {lot.start_exempt_year || 'N/A'}
                                        </Typography>
                                    </div>
                                </div>
                            ) : (
                                <div className='flex flex-row gap-4 w-full text-center'>
                                    <div className='flex flex-col w-full'>
                                        <select
                                            className="rounded-[7px] border-blue-gray-200"
                                            value={startExemptBimester}
                                            onChange={(e) => setStartExemptBimester(e.target.value)}
                                        >
                                            <option value="">Bimestre</option>
                                            {bim.map((bimester) => (
                                                <option key={bimester} value={bimester.toString()}>
                                                    {bimester}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className='flex flex-col w-full'>
                                        <select
                                            className="rounded-[7px] border-blue-gray-200"
                                            value={startExemptYear}
                                            onChange={(e) => setStartExemptYear(e.target.value)}
                                        >
                                            <option value="">Año</option>
                                            {startYears.map((year) => (
                                                <option key={year} value={year.toString()}>
                                                    {year}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='py-2'>
                            <Typography
                                variant="small"
                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                            >
                                {isExpiration ? 'Fin de exención' : 'Fin de exención (opcional, vacío = indefinida)'}
                            </Typography>
                            <div className='flex flex-row gap-4 w-full text-center'>
                                <div className='flex flex-col w-full'>
                                    <select
                                        className="rounded-[7px] border-blue-gray-200"
                                        value={endExemptBimester}
                                        onChange={(e) => setEndExemptBimester(e.target.value)}
                                    >
                                        <option value="">Bimestre</option>
                                        {bim.map((bimester) => (
                                            <option key={bimester} value={bimester.toString()}>
                                                {bimester}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className='flex flex-col w-full'>
                                    <select
                                        className="rounded-[7px] border-blue-gray-200"
                                        value={endExemptYear}
                                        onChange={(e) => setEndExemptYear(e.target.value)}
                                    >
                                        <option value="">Año</option>
                                        {endYears.map((year) => (
                                            <option key={year} value={year.toString()}>
                                                {year}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <h6 className="text-blue-gray-300 text-sm mt-3 mb-6 font-bold uppercase">
                            NOTAS DE MOVIMIENTOS DEL PREDIO
                        </h6>
                        <div className="flex flex-wrap">
                            <div className="w-full lg:w-12/12 px-0">
                                <div className="relative w-full mb-3">
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                    >
                                        Notas
                                    </Typography>
                                    <textarea
                                        type="text"
                                        className="border-0 px-3 py-3 placeholder-blueGray-300 text-blue-gray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                        value={lotNotes || ""}
                                        onChange={(e) => setLotNotes(e.target.value)}
                                        rows="6"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <Button
                            className="w-full bg-green-500 mt-4 hover:bg-green-600 text-white rounded-md px-5 py-3 font-semibold"
                            onClick={() => handleSubmit()}
                            disabled={loaderActive || (isExpiration && !hasFranchise)}
                        >
                            GUARDAR
                        </Button>
                        {
                            error && <p style={{color: "red"}}>{error}</p>
                        }
                    </CardBody>
                </Card>
            </div>
            <Dialog open={openEndDialog} handler={setOpenEndDialog}>
                <DialogHeader>Guardado</DialogHeader>
                <DialogBody>
                    <Typography>
                        {isExpiration
                            ? 'Se registró el vencimiento de la franquicia del predio'
                            : 'Se registró la franquicia del predio'}
                    </Typography>
                </DialogBody>
                <DialogFooter>
                    <Button
                        className="bg-green-500 text-white"
                        onClick={() => handleReset()}
                        disabled={loaderActive}
                    >
                        Finalizar
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    )
};

export default Franchise;
